import { DATE_PRESETS, UNBOUNDED, type DateFilterState, type DatePresetId } from '../../dateRange';

/**
 * Date filtering for the tournament list: nine presets grouped by grain — day, week, month —
 * each group running past, present, future so the row scans rather than reads.
 *
 * A preset and a custom range are mutually exclusive. Picking a preset leaves whatever dates
 * were typed in the custom fields intact, so switching back and forth doesn't lose them.
 */

const GROUPS: DatePresetId[][] = [
  ['yesterday', 'today', 'tomorrow'],
  ['lastWeek', 'thisWeek', 'nextWeek'],
  ['lastMonth', 'thisMonth', 'nextMonth'],
];

const LABEL = Object.fromEntries(DATE_PRESETS.map((p) => [p.id, p.label])) as Record<
  DatePresetId,
  string
>;

const chip = 'rounded-full px-2.5 py-1 text-xs transition';
const chipOn = `${chip} bg-felt-700 font-medium text-white`;
const chipOff = `${chip} border border-ink-800/15 text-ink-800/70 hover:border-felt-500`;
const dateInput =
  'border border-ink-800/15 bg-white px-2 py-1 text-xs text-ink-950 outline-none focus:border-felt-500';

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateFilterState;
  onChange: (next: DateFilterState) => void;
}) {
  // Clicking the active preset again clears it, so the row doubles as its own off switch.
  const selectPreset = (id: DatePresetId) =>
    onChange({ ...value, preset: value.preset === id ? null : id });

  const setCustom = (edge: 'start' | 'end', raw: string) =>
    onChange({ preset: null, custom: { ...value.custom, [edge]: raw === '' ? null : raw } });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" role="group" aria-label="Filter by date">
      {GROUPS.map((group, i) => (
        <div key={group[0]} className="flex items-center gap-1.5">
          {i > 0 ? <span className="mr-1.5 h-4 w-px bg-ink-800/10" aria-hidden /> : null}
          {group.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => selectPreset(id)}
              aria-pressed={value.preset === id}
              className={value.preset === id ? chipOn : chipOff}
            >
              {LABEL[id]}
            </button>
          ))}
        </div>
      ))}

      <span className="h-4 w-px bg-ink-800/10" aria-hidden />

      <div className="flex items-center gap-1.5">
        <label className="sr-only" htmlFor="tournament-date-start">
          Start date
        </label>
        <input
          id="tournament-date-start"
          type="date"
          title="Leave either side empty for an open-ended range"
          value={value.custom.start ?? ''}
          max={value.custom.end ?? undefined}
          onChange={(e) => setCustom('start', e.target.value)}
          className={dateInput}
        />
        <span className="text-xs text-ink-800/40">to</span>
        <label className="sr-only" htmlFor="tournament-date-end">
          End date
        </label>
        <input
          id="tournament-date-end"
          type="date"
          title="Leave either side empty for an open-ended range"
          value={value.custom.end ?? ''}
          min={value.custom.start ?? undefined}
          onChange={(e) => setCustom('end', e.target.value)}
          className={dateInput}
        />
        {value.custom.start || value.custom.end ? (
          <button
            type="button"
            onClick={() => onChange({ preset: value.preset, custom: UNBOUNDED })}
            className="text-xs text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
          >
            clear
          </button>
        ) : null}
      </div>
    </div>
  );
}
